import {useLocation, useNavigate, useParams} from "react-router-dom";

import {useRecoilState, useRecoilValue} from "recoil";
import {useEffect, useRef, useState} from "react";
import {selectedSchool} from "../../../recoils/recoil";
import api from "../../../services/api";

const SchoolChoice = () => {
    const {schoolName} = useParams();
    const [searchName, setSearchName] = useState(null);
    const navigate = useNavigate();

    // Separate states for all data and displayed data
    const [allSchoolList, setAllSchoolList] = useState([]); // All schools
    const [displayedSchoolList, setDisplayedSchoolList] = useState([]); // Currently displayed schools

    const [chosenSchools, setChosenSchools] = useRecoilState(selectedSchool);
    const term = useRef('');
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const selectedSchoolList = useRecoilValue(selectedSchool);
    const [selectedSchoolIds, setSelectedSchoolIds] = useState(
        selectedSchoolList.map(school => school.id)
    );

    // 모바일 툴팁 관련
    const [isTooltipVisible, setTooltipVisible] = useState(false);
    const toolTipRef = useRef(null);

    // 그루핑된 검색 결과
    const [groupedSchoolList, setGroupedSchoolList] = useState({});

    // Pagination states
    const [page, setPage] = useState(1);
    const limit = 100; // Display 100 items at a time
    const observerRef = useRef(null);

    useEffect(() => {
        const isNavigatingBack = location.state ? location.state.isSchoolBack : null;

        const getSchool = async () => {
            await setSearchName(schoolName);

            try {
                setIsLoading(true);
                const response = await api.post('/get_schools_by_school_name', {
                    name_text: schoolName
                });

                if (response.status === 200) {
                    const filteredData = response.data.data.map((school) => ({
                        school_id: school.school_id,
                        primaryname: school.primaryname,
                        primaryname_ko: school.primaryname_ko,
                        name_list: school.name_list,
                        n_books: school.n_books,
                        n_events: school.n_events,
                        n_orders: school.n_orders,
                        n_regions: school.n_regions,
                        n_tablerecords: school.n_tablerecords,
                        n_tocrecords: school.n_tocrecords,
                        n_tocs: school.n_tocs,
                        n_tables: school.n_tables,
                    }));

                    const sortedData = filteredData.sort((a, b) => {
                        if (a.primaryname_ko < b.primaryname_ko) return -1;
                        if (a.primaryname_ko > b.primaryname_ko) return 1;
                        return 0;
                    });

                    setAllSchoolList(sortedData);
                    setDisplayedSchoolList(sortedData.slice(0, limit));
                    setTotalCount(sortedData.length);
                }
            } catch (error) {
                console.error(error);
            } finally {
                setIsLoading(false);
            }
        };

        if (isNavigatingBack === null) {
            getSchool();
        }

        // 모바일 툴팁 외부 영역 클릭 시 툴팁 닫기
        document.addEventListener("click", handleClickOutside);
        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, [schoolName, location.state]);

    // 무한 스크롤 Intersection Observer 설정
    useEffect(() => {
        const currentObserverRef = observerRef.current; // Store ref value
        const loadMoreData = () => {
            const nextPage = page + 1;
            const startIndex = page * limit;
            const endIndex = nextPage * limit;

            const moreData = allSchoolList.slice(startIndex, endIndex);
            setDisplayedSchoolList(prev => [...prev, ...moreData]);
            setPage(nextPage);
        };

        // Observer의 콜백함수 선언
        const onIntersection = (entries) => {

            const entry = entries[0];  // 첫 번째 entry만 확인
            if (entry.isIntersecting && displayedSchoolList.length < allSchoolList.length) {
                loadMoreData();
            }

        };

        const observer = new IntersectionObserver(onIntersection, {
            root: null,
            rootMargin: '100px',  // 뷰포트 아래 100px 지점에서 미리 로드 시작
            threshold: 0.1  // 10%만 보여도 로드 시작
        });

        if (currentObserverRef) {
            observer.observe(currentObserverRef);
        }

        return () => {
            if (currentObserverRef) {
                observer.unobserve(currentObserverRef);
            }
            observer.disconnect();
        };
    }, [allSchoolList, page, limit]);

    const handleClickOutside = (e) => {
        if (toolTipRef.current && !toolTipRef.current.contains(e.target)) {
            setTooltipVisible(false);
        }
    };


    const selectSchool = (schoolId, personName, personNameKo) => {
        setSelectedSchoolIds(prevIds =>
            prevIds.includes(schoolId) ? prevIds.filter(id => id !== schoolId) : [...prevIds, schoolId]
        );

        setChosenSchools(prevData => {
            const isSchoolSelected = prevData.some(school => school.id === schoolId);

            if (isSchoolSelected) {
                return prevData.filter(school => school.id !== schoolId);
            } else {
                return [...prevData, {
                    id: schoolId,
                    name: personName,
                    nameKo: personNameKo,
                }];
            }
        });
    }

    const searchSchool = () => {
        let idList = [];
        chosenSchools.forEach(school => {
            idList.push(school.id);
        })
        navigate(`/school/${idList}`);
    }

    const handleDeleteSchool = (schoolId) => {
        setSelectedSchoolIds(prevIds => prevIds.filter(id => id !== schoolId));
        setChosenSchools(prevData => prevData.filter(school => school.id !== schoolId));
    }

    const handleClearSchool = () => {
        setSelectedSchoolIds([])
        setChosenSchools([])
    }

    const handleTerm = (e) => {
        term.current = e.target.value;
    }

    const handleSearch = () => {
        if (term.current.length > 0) {
            getOtherSchool();
        }
    }

    const onKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSearch();
        }
    };

    const getOtherSchool = async () => {
        await setSearchName(term.current);
        try {
            setIsLoading(true);
            const response = await api.post('/get_schools_by_school_name', {
                name_text: term.current
            });

            if (response.status === 200) {
                const filteredData = response.data.data.map((school) => ({
                    school_id: school.school_id,
                    primaryname: school.primaryname,
                    primaryname_ko: school.primaryname_ko,
                    name_list: school.name_list,
                    n_books: school.n_books,
                    n_events: school.n_events,
                    n_orders: school.n_orders,
                    n_regions: school.n_regions,
                    n_tablerecords: school.n_tablerecords,
                    n_tocrecords: school.n_tocrecords,
                    n_tocs: school.n_tocs,
                    n_tables: school.n_tables,
                }));

                setAllSchoolList(filteredData);
                setDisplayedSchoolList(filteredData.slice(0, limit));
                setTotalCount(filteredData.length);
                setPage(1);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    // 검색 결과를 primaryname으로 그룹화하는 함수
    const groupSchoolsByPrimaryName = (schools) => {
        return schools.reduce((acc, school) => {
            if (!acc[school.primaryname]) {
                acc[school.primaryname] = [];
            }
            acc[school.primaryname].push(school);
            return acc;
        }, {});
    };

    // 그룹 전체를 선택/해제하는 함수
    const selectGroup = (primaryname) => {
        const group = groupedSchoolList[primaryname];
        const allSelected = group.every(school => selectedSchoolIds.includes(school.school_id));

        if (allSelected) {
            // 모두 선택되어 있다면 전체 해제
            setSelectedSchoolIds(prevIds => prevIds.filter(id => !group.some(school => school.school_id === id)));
            setChosenSchools(prevData => prevData.filter(school => !group.some(groupSchool => groupSchool.school_id === school.id)));
        } else {
            // 일부만 선택되어 있거나 전체 미선택이면 전체 선택
            const newIds = group.map(school => school.school_id);
            setSelectedSchoolIds(prevIds => [...new Set([...prevIds, ...newIds])]);
            setChosenSchools(prevData => {
                const newSchools = group.map(school => ({
                    id: school.school_id,
                    name: school.primaryname,
                    nameKo: school.primaryname_ko,
                }));
                return [...prevData.filter(p => !newIds.includes(p.id)), ...newSchools];
            });
        }
    };

    // displayedPersonList가 변경될 때마다 그룹화된 결과 업데이트
    useEffect(() => {
        setGroupedSchoolList(groupSchoolsByPrimaryName(displayedSchoolList));
    }, [displayedSchoolList]);

    // 검색창 필요 검색 결과를 페이지 이동하지 말고 아래에 뿌리기
    return (
        <main id="contents">
            <div className="sub-content">
                <div className="search-wrap">
                    <div className="cont-top">
                        <div className="w-set">
                            <div className="title-wrap">
                                <h2 className="title batang">공간</h2>
                                <p className="text">조선제학교일람을 통해 <br/>일제강점기 조선총독부가 조선의 교육 상황을 기록한 자료를 확인 할 수 있습니다.</p>
                            </div>
                        </div>
                    </div>
                    <div className="cont-btm">
                        <div className="w-set">
                            <div className="search-list-wrap">
                                <div className="search-top">
                                    <div className="text-wrap">
                                        <p>학교</p>
                                        <div className="tooltip-wrap" ref={toolTipRef}>
                                            <button type="button" className="i-tool"
                                                    onClick={() => setTooltipVisible(!isTooltipVisible)}
                                            ></button>
                                            <div className={`tool-box ${isTooltipVisible ? 'on' : ''}`}>
                                                원본의 이름 오기, 한자 폰트 등의 이유로 한 학교가 여러 이름을 가질 수 있습니다. <br/>여러 학교로 검색하여 자료를
                                                모아보세요. 새로운 학교로 검색해도 선택한 학교는 유지됩니다.
                                            </div>
                                        </div>
                                    </div>
                                    <div className="search-wrap">
                                        <form>
                                            <div className="search-box">
                                                <div className="search-area">
                                                    <div className="input-wrap">
                                                        <label htmlFor="search">
                                                            <input
                                                                type="text"
                                                                name="search"
                                                                placeholder="학교명을 입력해주세요."
                                                                onChange={handleTerm}
                                                                onKeyDown={onKeyPress}
                                                            />
                                                        </label>
                                                        <button type="button" onClick={handleSearch}
                                                                className="btn-search">검색
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                                <div className="search-btm">
                                    <form>
                                        <div className="filter-wrap">
                                            <div className="filter-left">
                                                <div className="total-wrap">
                                                    <p>선택한 학교 총 <span>{chosenSchools.length}</span></p>
                                                    <button type="reset" className="btn-reset"
                                                            onClick={() => handleClearSchool()}>초기화
                                                    </button>
                                                </div>
                                                <div className="name-wrap">
                                                    <ul className="name-list">
                                                        {chosenSchools.map((school, index) => (
                                                            <li key={school.id}>
                                                                <span className="num">{index + 1}</span>
                                                                <p className="name">{school.nameKo === school.name ? school.name : school.name + school.nameKo} </p>
                                                                <button
                                                                    type="button"
                                                                    className="btn-delete"
                                                                    onClick={() => handleDeleteSchool(school.id)}
                                                                >
                                                                    삭제
                                                                </button>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                    <div className="btn-wrap">
                                                        <button className="btn" onClick={searchSchool}><i
                                                            className="i-search"></i>자료검색
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="filter-right">
                                                <div className="legend-wrap legend-indicate">
                                                    <ul className="legend-area">
                                                        <li><i className="books"></i>책</li>
                                                        <li><i className="tocs"></i>목차</li>
                                                        <li><i className="tocrecords"></i>책내용</li>
                                                        <li><i className="tables"></i>표</li>
                                                        <li><i className="tablerecords"></i>표내용</li>
                                                        <li><i className="orders"></i>직원록/인사명령</li>
                                                        <li><i className="events"></i>회고록 내용</li>
                                                        <li><i className="regions"></i>지리</li>
                                                    </ul>
                                                </div>
                                                {/* 25.02.10 첫 진입 시 보이는 화면 */}
                                                <div className="before-searching"
                                                     style={{display: isLoading === false && searchName === null ? 'block' : 'none'}}>
                                                    <div className="empty person">
                                                        <p>학교명을 입력하시면 검색결과가 나옵니다.</p>
                                                    </div>
                                                </div>

                                                <div className="result-wrap">

                                                    <p className="result-text"
                                                       style={{display: isLoading === false && searchName !== null ? 'block' : 'none'}}>
                                                        {/*검색어는 span 태그 안에 추가*/}
                                                        <span>&#8220;{searchName}&#8220;</span>
                                                        로 검색하신 결과는 <em>총 {totalCount}건</em> 입니다.
                                                    </p>

                                                    {Object.entries(groupedSchoolList).map(([primaryname, group]) => (
                                                        <div className="section-wrap" key={primaryname}>
                                                            <div>
                                                                {/* 전체 선택 */}
                                                                <div className="section-title">
                                                                    <div className="checkbox">
                                                                        <label>
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={group.every(school => selectedSchoolIds.includes(school.school_id))}
                                                                                onChange={() => selectGroup(primaryname)}
                                                                            />
                                                                            <div className="label">
                                                                                <p className="name">
                                                                                    <span
                                                                                        className="bold">{primaryname}</span>
                                                                                </p>
                                                                            </div>
                                                                        </label>
                                                                    </div>
                                                                </div>

                                                                <div className="table-wrap"
                                                                     style={{display: isLoading === false ? 'block' : 'none'}}>

                                                                    {group.map((school) => (
                                                                        <div className="list-row"
                                                                             key={school.school_id}>
                                                                            <div className="checkbox">
                                                                                <label
                                                                                    htmlFor={`school_${school.school_id}`}>
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        name="school"
                                                                                        id={`school_${school.school_id}`}
                                                                                        checked={selectedSchoolIds.includes(school.school_id)}
                                                                                        onChange={() =>
                                                                                            selectSchool(
                                                                                                school.school_id,
                                                                                                school.primaryname,
                                                                                                school.primaryname_ko
                                                                                            )
                                                                                        }
                                                                                    />
                                                                                    <div className="label">
                                                                                        <p className="name">
                                                                                        <span
                                                                                            className="bold">{school.primaryname}</span>
                                                                                        </p>
                                                                                    </div>
                                                                                </label>
                                                                            </div>

                                                                            <div className="sub-name">
                                                                                {/* 중복 필터링 후 렌더링 */}
                                                                                {school.name_list.filter(name => name !== school.primaryname) // primaryname_ko 만 제거된 배열 생성
                                                                                    // 필터링된 배열에 map 수행
                                                                                    .map(name => (
                                                                                        <span key={name}>{name}</span>
                                                                                    ))}
                                                                            </div>
                                                                            <div className="legend-wrap">
                                                                                <ul className="legend-area">
                                                                                    <li style={{display: school.n_tocrecords !== 0 ? "flex" : "none"}}>
                                                                                        <i className="books"
                                                                                        ></i>{school.n_tocrecords !== 0 ? school.n_tocrecords : ""}
                                                                                    </li>
                                                                                    <li style={{display: school.n_tocrecords !== 0 ? "flex" : "none"}}>
                                                                                        <i className="tocs"></i>{school.n_tocrecords !== 0 ? school.n_tocrecords : ""}
                                                                                    </li>
                                                                                    <li style={{display: school.n_tocrecords !== 0 ? "flex" : "none"}}>
                                                                                        <i className="tocrecords"
                                                                                        ></i>{school.n_tocrecords !== 0 ? school.n_tocrecords : ""}
                                                                                    </li>
                                                                                    <li style={{display: school.n_tables !== 0 ? "flex" : "none"}}>
                                                                                        <i className="tables"
                                                                                        ></i>{school.n_tables !== 0 ? school.n_tables : ""}
                                                                                    </li>
                                                                                    <li style={{display: school.n_tablerecords !== 0 ? "flex" : "none"}}>
                                                                                        <i className="tablerecords"
                                                                                        ></i>{school.n_tablerecords !== 0 ? school.n_tablerecords : ""}
                                                                                    </li>
                                                                                    <li style={{display: school.n_orders !== 0 ? "flex" : "none"}}>
                                                                                        <i className="orders"
                                                                                        ></i>{school.n_orders !== 0 ? school.n_orders : ""}
                                                                                    </li>

                                                                                    <li style={{display: school.n_events !== 0 ? "flex" : "none"}}>
                                                                                        <i className="events"
                                                                                        ></i>{school.n_events !== 0 ? school.n_events : ""}
                                                                                    </li>

                                                                                    <li style={{display: school.n_regions !== 0 ? "flex" : "none"}}>
                                                                                        <i className="regions"
                                                                                        ></i>{school.n_regions !== 0 ? school.n_regions : ""}
                                                                                    </li>

                                                                                </ul>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                            {/* 무한 스크롤을 위한 참조 요소 추가 */}
                                                            <div ref={observerRef} style={{height: '10px'}}></div>
                                                </div>
                                                {/*안보일 때 : display: none / 로딩중일 때 display: block */}
                                                <div className="load-wrap"
                                                     style={{display: isLoading === true ? 'block' : 'none'}}>
                                                    <div className="load-box">
                                                        <div className="square"></div>
                                                        <div className="square"></div>
                                                        <div className="square"></div>
                                                        <div className="square"></div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
}

export default SchoolChoice;